import { debounce } from "lodash";
import { useCallback, useContext, useRef, useState } from "react";
import {
	components,
	GroupBase,
	MenuListProps,
	OptionsOrGroups,
} from "react-select";
import AsyncSelect from "react-select/async";
import { Constants } from "../../../../../common/constants";
import { Mentionable } from "../../../../../common/sesh_types";
import { trpc } from "../../utils/trpc";
import {
	dropdownColourStyles,
	DropdownMemberLabel,
	DropdownMemberSelectedLabel,
	SingleValue,
} from "../searchable_dropdown";
import {
	GuildMemberContext,
	useGuildMemberQuery,
} from "./hooks/use_guild_members";

function MemberToDropdownItem(
	members_map: Record<string, Mentionable>,
	id: string
) {
	const member = members_map[id];

	return {
		label: <DropdownMemberLabel val={member} />,
		selectedLabel: <DropdownMemberSelectedLabel val={member} />,
		value: id,
		color: "#ffffff",
	};
}

const MenuList = (props: MenuListProps) => {
	return (
		<components.MenuList {...props}>
			{props.children}
			{props.options.length > Constants.GUILD_MEMBERS_SEARCH_LIMIT && (
				<p className="py-2 text-center text-xs font-bold text-sesh-primary">
					More results than can be displayed. Type to filter.
				</p>
			)}
		</components.MenuList>
	);
};

const Input = (props) => <components.Input {...props} maxLength={64} />;

export function MembersSearchDropdown(props: {
	guild_id: string;
	selected_ids: string[];
	disabled_ids?: string[];
	limit?: number;
	onUpdate: (selected_ids: string[]) => void;
	is_disabled?: boolean;
}) {
	const context = trpc.useContext();

	const search_cache = useRef<
		Record<string, { label: string; value: string }[]>
	>({});

	const members = useGuildMemberQuery(props.guild_id, props.selected_ids);

	const members_cache = useContext(GuildMemberContext);
	const members_for_guild = members_cache.getMembers(props.guild_id);

	const members_map: Record<string, Mentionable> = {};
	for (const member of members_for_guild) {
		members_map[member.id] = member;
	}

	const fetchMembers = useCallback(
		debounce(
			(
				new_input: string,
				callback: (
					options: OptionsOrGroups<unknown, GroupBase<unknown>>
				) => void
			) => {
				if (search_cache.current[new_input]) {
					callback(search_cache.current[new_input]);
					return;
				}

				const previous_searches = Object.keys(search_cache.current);
				for (const search of previous_searches) {
					if (search.length === 0) {
						continue;
					}

					// This can be improved by checking for length < Constants.GUILD_MEMBERS_SEARCH_LIMIT, but it gets tricky to deal with since search deals with usernames and displaynames
					if (
						new_input.startsWith(search) &&
						search_cache.current[search].length === 0
					) {
						callback([]);
						return;
					}
				}

				return context.members.search
					.fetch({
						guild_id: props.guild_id,
						query: new_input,
					})
					.then((val) => {
						members_cache.addMembers(props.guild_id, val);

						const member_items = val.map((member) => ({
							label: member.name,
							value: member.id,
						}));

						search_cache.current[new_input] = member_items;

						callback(member_items);
					});
			},
			500
		),
		[props.guild_id]
	);

	return (
		<div className="flex flex-col gap-2">
			{props.limit && props.selected_ids.length >= props.limit && (
				<div className="error-text">
					Selection limit reached ({props.limit} / {props.limit})
				</div>
			)}
			<AsyncSelect
				placeholder="Select members..."
				classNamePrefix="sesh-dropdown"
				isMulti
				styles={dropdownColourStyles}
				components={{ Input, SingleValue, MenuList }}
				value={props.selected_ids.map((val) =>
					MemberToDropdownItem(members_map, val)
				)}
				onChange={(data) => {
					if (props.is_disabled) {
						return;
					}
					props.onUpdate(
						data
							.slice(0, props.limit ?? 100)
							.map((val) => val.value)
					);
				}}
				loadOptions={(input, callback) => {
					if (props.is_disabled) {
						callback([]);
						return;
					}

					fetchMembers(input, callback);
				}}
				isDisabled={props.is_disabled}
				defaultOptions={true}
				isOptionDisabled={(data) =>
					props.disabled_ids?.includes(data.value)
				}
				formatOptionLabel={(data) => {
					const member = members_map[data.value];
					return member ? (
						<DropdownMemberLabel
							val={member}
							disabled={
								props.disabled_ids?.includes(data.value)
									? {
											text_element: "Already RSVPed",
											class: "dropdown-disabled-text",
									  }
									: undefined
							}
						/>
					) : (
						data.value
					);
				}}
			/>
		</div>
	);
}
